import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import _extends from "@babel/runtime/helpers/esm/extends";
const _excluded = ["name", "type", "forceValidate"],
  _excluded2 = ["children", "debug", "diff", "errorMessages", "errorsDisableSubmit", "fieldStateReducer", "formElement", "initialFieldState"];
/* eslint-disable react/destructuring-assignment */

/* eslint-disable eqeqeq */
/* eslint-disable no-underscore-dangle */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { genUID, isObject } from '@entrata/utils';
import { formContext } from "./formContext";
import { FormField } from "./FormField";
import { FormActions } from "./FormActions";
import { FormSpy } from "./FormSpy";
import { FormRow } from "./FormRow";
import { isValueEmpty } from "../utils";
import { errorTypes } from "../types";
import { errorMessagesShape } from "./shapes/errorMessages.shape";
import { jsx as ___EmotionJSX } from "@emotion/core";
class FormValidationError {
  constructor(errors) {
    this.name = 'FormValidationError';
    this.fields = errors;
  }
}

/** **
 * This component keeps all necessary form and field state and uses Context
 * to make that state accessible in the common
 * fields.
 * */
class Form extends Component {
  constructor(props) {
    var _this;
    super(props);
    _this = this;
    /** Retrieves the initialization object. */
    this.getInitializer = initialFieldState => {
      const initializer = this.isFunc(initialFieldState) ? initialFieldState() : initialFieldState;
      if (!isObject(initializer)) {
        console.error('The Form requires an initialization object for each field it manages. You can pass either an initialization object or a function that returns your initialization object.');
      }
      return initializer;
    };
    this.isFieldChanged = false;
    /** Gets initial field state by merging with defaults and, if needed, initializing error state. */
    this.getInitializedFields = obj => Object.entries(obj).reduce((fields, _ref) => {
      let [name, field] = _ref;
      let initializedField = {
        active: false,
        dirty: false,
        error: '',
        ignore: false,
        required: false,
        touched: false,
        value: undefined
      };
      if (isObject(field)) {
        initializedField = _extends({}, initializedField, field, {
          touched: !!(field.hasOwnProperty('value') && field && field.value),
          error: field.required && isValueEmpty(field && field.value) ? errorTypes.required : ''
        });
      } else {
        initializedField.value = field;
        initializedField.touched = !isValueEmpty(field);
      }
      this.props.debug && console.log('FORM - initializing fields', {
        name,
        field,
        initializedField
      });
      return _extends({}, fields, {
        [name]: initializedField
      });
    }, {});
    /** Changes for decimal precision end */
    /** Handles updates to field state. */
    this.handleUpdate = _ref2 => {
      let {
          name,
          type
        } = _ref2,
        data = _objectWithoutPropertiesLoose(_ref2, _excluded);
      const {
        debug,
        fieldStateReducer
      } = this.props;
      // Log error if an unregistered field tries to update field state.
      const {
        fields,
        setValuesOfFields
      } = this.state;
      if (!isObject(this.initialFieldState[name])) {
        console.error(`You tried to update a field that doesn't exist in the Form state. Make sure ${name} is included in initialFieldState.`);
      }
      const updateFormState = formData => {
        const updatedFields = _extends({}, fields, formData);
        const update = {
          fields: updatedFields,
          values: this.getValues(updatedFields),
          hasErrors: this.hasErrors(updatedFields)
        };
        debug && console.log('FORM - handleUpdate', update);
        this.setState(update);
      };
      const updatedField = {
        [name]: _extends({}, fields[name], data, {
          dirty: data.hasOwnProperty('value') ? this.initialFieldState[name] && this.initialFieldState[name].value !== data.value : fields[name].dirty
        })
      };
      if (this.isFunc(fieldStateReducer)) {
        const changes = fieldStateReducer({
          state: fields,
          changes: {
            name,
            type,
            payload: updatedField
          },
          setValuesOfFields
        });
        if (Promise.prototype.isPrototypeOf(changes)) {
          changes.then(formData => {
            updateFormState(formData);
          }).catch(err => {
            console.error('complete error is ', err);
          });
        } else {
          updateFormState(changes);
        }
      } else {
        updateFormState(updatedField);
      }
    };
    /** Returns an object with the value of each field */
    this.getValues = function (fields) {
      if (fields === void 0) {
        fields = {};
      }
      const {
        diff
      } = _this.props;
      return Object.entries(fields).reduce((vals, _ref3) => {
        let [name, field] = _ref3;
        if (!field.ignore && !diff || field.value !== _this.initialFieldState[name].value) {
          return _extends({}, vals, {
            [name]: field.value
          });
        }
        return vals;
      }, {});
    };
    /** Returns true if the form has any fields in error state, otherwise false. */
    this.hasErrors = fields => {
      return Boolean(Object.keys(fields).find(key => !!fields[key].error && !fields[key].ignore));
    };
    /** Resets form to initialFieldState */
    this.reset = () => {
      this.setState({
        fields: this.props.initialFieldState,
        hasErrors: this.hasErrors(this.props.initialFieldState)
      });
    };
    this.handleSubmitErrors = fields => {
      this.setState(prev => {
        const updatedFields = _extends({}, prev.fields, Object.entries(fields).reduce((obj, _ref4) => {
          let [name, error] = _ref4;
          // only add error for existing fields
          if (prev.fields[name]) {
            return _extends({}, obj, {
              [name]: _extends({}, prev.fields[name], {
                error,
                touched: true
              })
            });
          }
          return obj;
        }, {}));
        return {
          fields: updatedFields,
          hasErrors: this.hasErrors(updatedFields)
        };
      });
    };
    /** Submit handler that prevents default and provides some extra default functionality. */
    this.handleSubmit = e => {
      if (e) {
        e.preventDefault();
        e.stopPropagation();
      }
      const {
        errorsDisableSubmit,
        onSubmit
      } = this.props;
      if (!errorsDisableSubmit || errorsDisableSubmit && !this.state.hasErrors) {
        try {
          typeof onSubmit === 'function' && onSubmit(this.state, this.handleSubmitErrors);
        } catch (err) {
          if (err.name === 'FormValidationError') {
            this.handleSubmitErrors(err.fields);
          } else {
            throw err;
          }
        }
      } else {
        this.touchAllTheFields();
      }
    };
    /** Sets each fields touched state to true */
    this.touchAllTheFields = () => {
      this.setState(_ref5 => {
        let {
          fields
        } = _ref5;
        return {
          fields: Object.keys(fields).reduce((obj, key) => _extends({}, obj, {
            [key]: _extends({}, fields[key], {
              touched: true
            })
          }), {})
        };
      });
    };
    /** Used for Form implementations that don't use a form element. */
    this.handleKeyDown = e => {
      const {
        onKeyDown
      } = this.props;
      if (this.form.current.id === this.id) {
        if (e.keyCode === 13) {
          e.preventDefault();
          e.stopPropagation();
          this.handleSubmit(e);
        }
        typeof onKeyDown === 'function' && onKeyDown(e, this.state);
      }
    };
    this.count = 0;
    this.setValuesOfFields = (fieldValueObject, callBackFn) => {
      this.setState(_ref6 => {
        let {
          fields
        } = _ref6;
        const changesInFields = Object.entries(fieldValueObject).reduce((resultant, item) => {
          var _this$initialFieldSta;
          return _extends({}, resultant, {
            [item[0]]: _extends({}, fields[item[0]], item[1], {
              dirty: item[1].hasOwnProperty('value') ? ((_this$initialFieldSta = this.initialFieldState[item[0]]) === null || _this$initialFieldSta === void 0 ? void 0 : _this$initialFieldSta.value) !== item[1].value : fields[item[0]].dirty
            })
          });
        }, {});
        const updatedFields = _extends({}, fields, changesInFields);
        return {
          fields: updatedFields,
          values: this.getValues(updatedFields),
          hasErrors: this.hasErrors(updatedFields)
        };
      }, callBackFn);
    };
    props.debug && console.log('Constructing Form', props);
    this.form = /*#__PURE__*/React.createRef();
    this.id = props.id || `form-${genUID()}`;

    // Initialize form state based on props.initialFieldState
    const _initializer = this.getInitializer(props.initialFieldState);
    this.nameProps = {};
    this.fields = this.getInitializedFields(_initializer);
    this.initialFieldState = this.fields;
    Object.keys(_initializer).forEach(name => this.getChild(this.props.children, name));
    this.fields = this.currencyFieldsUpdate(this.fields);
    this.state = {
      errorMessages: props.errorMessages,
      fields: this.fields,
      hasErrors: this.hasErrors(this.fields),
      values: this.getValues(this.fields),
      onUpdate: this.handleUpdate,
      onSubmit: this.handleSubmit,
      reset: this.reset,
      setValuesOfFields: this.setValuesOfFields
    };
  }
  isFunc(arg) {
    return typeof arg === 'function';
  }
  UNSAFE_componentWillUpdate(nextProps, nextState) {
    const dynamicFields = this.isFunc(nextProps.initialFieldState) ? nextProps.initialFieldState() : nextProps.initialFieldState;

    // Comparing with initialFieldState to check whether the fields are changed
    const prevObj = Object.keys(this.initialFieldState);
    const newObj = Object.keys(dynamicFields);
    const isChanged = newObj.filter(el => {
      return !prevObj.includes(el);
    }).length;
    const viceVersa = prevObj.filter(el => {
      return !newObj.includes(el);
    }).length;

    // If fields are added dynamically then we are updating the component, otherwise leave as it is.
    if (isChanged || viceVersa) {
      // Checking the newly updated fields having the data or not
      // eslint-disable-next-line no-shadow
      const prevObj = Object.keys(nextState.fields);
      // eslint-disable-next-line no-shadow
      const newObj = Object.keys(dynamicFields);
      const updatedFields = newObj.filter(el => {
        return !prevObj.includes(el);
      });
      const newFields = updatedFields.reduce((prev, cur) => {
        const field = dynamicFields[cur];
        if (field) {
          return _extends({}, prev, {
            [cur]: _extends({}, field)
          });
        }
        return prev;
      }, {});

      // Initializing the newly entered fields state
      const {
        fields
      } = this.state;
      const mergeFields = Object.keys(fields).reduce((prev, cur) => {
        const field = dynamicFields[cur];
        if (field) {
          return _extends({}, prev, {
            [cur]: _extends({}, fields[cur], {
              required: field.required
            })
          });
        }
        return prev;
      }, {});
      this.initialFieldState = this.getInitializedFields(_extends({}, mergeFields, newFields));
      this.isFieldChanged = false;

      // Updating the forms state if the fields are changed dynamically
      // eslint-disable-next-line react/no-will-update-set-state
      this.setState({
        fields: this.initialFieldState,
        values: this.getValues(this.initialFieldState),
        hasErrors: this.hasErrors(this.initialFieldState)
      });
      return true;
    }
    return false;
  }
  /** Changes for decimal precision start */
  currencyFieldsUpdate(fields) {
    Object.keys(fields).forEach(name => {
      var _window, _window2, _window2$__LOCALE_DAT, _window3, _window4, _window4$__LOCALE_DAT;
      let currencyValue;
      let decimalPlaces;
      const rawValue = fields[name] && String(fields[name].value);
      const getNameProps = this.nameProps && name && this.nameProps[name];

      // eslint-disable-next-line valid-typeof
      const isCurrency = getNameProps && getNameProps.hasOwnProperty('allowCurrencyPrecision');

      // eslint-disable-next-line valid-typeof
      const allowCurrencyPrecision = getNameProps && getNameProps.allowCurrencyPrecision;

      // eslint-disable-next-line valid-typeof
      const requireDecimal = getNameProps && getNameProps.requireDecimal;
      if (fields[name] && fields[name].value && String(fields[name].value).length > 0 && isCurrency) {
        if (String(fields[name].value).includes('.')) {
          decimalPlaces = String(fields[name].value).substring(String(fields[name].value).lastIndexOf('.') + 1).length;
        } else {
          decimalPlaces = 0;
        }
        fields[name].value = typeof window !== 'undefined' && window.i18nUtils.currency(fields[name].value, `p:${decimalPlaces};overridePrecision: true`);
      }
      if (fields[name] && fields[name].value && String(fields[name].value).length > 0 && requireDecimal && isCurrency && !rawValue.includes('.') && !(typeof window !== 'undefined' && (_window = window) !== null && _window !== void 0 && _window.__LOCALE_DATA__ && (_window2 = window) !== null && _window2 !== void 0 && (_window2$__LOCALE_DAT = _window2.__LOCALE_DATA__) !== null && _window2$__LOCALE_DAT !== void 0 && _window2$__LOCALE_DAT.boolAllowDecimalPrecision && allowCurrencyPrecision)) {
        fields[name].value = i18nUtils.currency(rawValue, 'p:2;overridePrecision:true');
      }
      if (fields[name] && fields[name].value && typeof window !== 'undefined' && (_window3 = window) !== null && _window3 !== void 0 && _window3.__LOCALE_DATA__ && (_window4 = window) !== null && _window4 !== void 0 && (_window4$__LOCALE_DAT = _window4.__LOCALE_DATA__) !== null && _window4$__LOCALE_DAT !== void 0 && _window4$__LOCALE_DAT.boolAllowDecimalPrecision && allowCurrencyPrecision) {
        var _window5, _window5$__LOCALE_DAT, _window6, _window6$__LOCALE_DAT;
        if (((_window5 = window) === null || _window5 === void 0 ? void 0 : (_window5$__LOCALE_DAT = _window5.__LOCALE_DATA__) === null || _window5$__LOCALE_DAT === void 0 ? void 0 : _window5$__LOCALE_DAT.decimalPrecision) == '1') {
          currencyValue = rawValue && String(rawValue.length) > 0 && Number.isInteger(Number(rawValue)) ? Number(rawValue) : rawValue;
          decimalPlaces = Number.isInteger(Number(rawValue)) ? 0 : String(currencyValue).substring(String(currencyValue).lastIndexOf('.') + 1).length;
        }
        if (((_window6 = window) === null || _window6 === void 0 ? void 0 : (_window6$__LOCALE_DAT = _window6.__LOCALE_DATA__) === null || _window6$__LOCALE_DAT === void 0 ? void 0 : _window6$__LOCALE_DAT.decimalPrecision) == '0.01') {
          currencyValue = rawValue && String(rawValue).length > 0 ? Number(rawValue).toFixed(2) : rawValue;
          decimalPlaces = String(currencyValue).substring(String(currencyValue).lastIndexOf('.') + 1).length;
          decimalPlaces = decimalPlaces > 1 ? decimalPlaces : 2;
        }
        fields[name].value = currencyValue && String(currencyValue).length > 0 ? typeof window !== 'undefined' && window.i18nUtils.currency(currencyValue, `p:${decimalPlaces};overridePrecision:true`) : currencyValue;
      }
    });
    return fields;
  }
  getChild(children, name) {
    if (children && Array.isArray(children)) {
      children.forEach(child => {
        if (child && child.props && child.props.name) {
          this.nameProps = _extends({}, this.nameProps, {
            [child.props.name]: (child.props.render || child.props.children)(_extends({}, this.fields[name], {
              setValuesOfFields: this.setValuesOfFields,
              onUpdate: this.handleUpdate,
              errorMessages: this.props.errorMessages
            })).props
          });
        } else {
          child && child.props && child.props.render && this.getChild(child.props.render({
            errorMessages: this.props.errorMessages,
            fields: this.fields,
            hasErrors: this.hasErrors(this.fields),
            values: this.getValues(this.fields),
            onUpdate: this.handleUpdate,
            onSubmit: this.handleSubmit,
            setValuesOfFields: this.setValuesOfFields,
            reset: this.reset
          }), name);
          child && child.props && child.props.children && this.getChild(child.props.children, name);
        }
      });
    } else if (children && children.props && children.props.name) {
      this.nameProps = _extends({}, this.nameProps, {
        // eslint-disable-next-line max-len
        [children.props.name]: (children.props.children || children.props.render)(_extends({}, this.fields[name], {
          setValuesOfFields: this.setValuesOfFields,
          onUpdate: this.handleUpdate,
          errorMessages: this.props.errorMessages
        })).props
      });
    } else {
      children && children.props && children.props.render && this.getChild(children.props.render({
        errorMessages: this.props.errorMessages,
        fields: this.fields,
        hasErrors: this.hasErrors(this.fields),
        values: this.getValues(this.fields),
        onUpdate: this.handleUpdate,
        onSubmit: this.handleSubmit,
        setValuesOfFields: this.setValuesOfFields,
        reset: this.reset
      }), name);
      // eslint-disable-next-line max-len
      children && children.props && children.props.children && this.getChild(children.props.children, name);
    }
  }
  render() {
    const _this$props = this.props,
      {
        children
        // eslint-disable-line no-unused-vars
        // eslint-disable-line no-unused-vars
        // eslint-disable-line no-unused-vars
        // eslint-disable-line no-unused-vars
        ,

        // eslint-disable-line no-unused-vars
        formElement
        // eslint-disable-line no-unused-vars
      } = _this$props,
      rest = _objectWithoutPropertiesLoose(_this$props, _excluded2);
    const {
      disabled
    } = this.state;
    const FormComponent = formElement;
    return ___EmotionJSX(FormComponent, _extends({}, rest, {
      id: this.id,
      onSubmit: this.handleSubmit,
      disabled: disabled,
      noValidate: true,
      onKeyDown: this.handleKeyDown,
      tabIndex: formElement !== 'form' ? -1 : null,
      ref: this.form
    }), ___EmotionJSX(formContext.Provider, {
      value: this.state
    }, children));
  }
}
Form.Spy = FormSpy;
Form.Field = FormField;
Form.Actions = FormActions;
Form.Row = FormRow;
Form.propTypes = {
  /** `on` allows browsers to autoComplete the form. */
  autoComplete: PropTypes.oneOf(['on', 'off']),
  /** Pass your fields as children */
  children: PropTypes.node.isRequired,
  /** Class that gets added to the `formElement`. */
  className: PropTypes.string,
  /** if you want the form to return only the values that have changed, this should be `true` */
  diff: PropTypes.bool,
  /** Disables the entire form. */
  disabled: PropTypes.bool,
  /** A map of the error types to the messages you want to render.
   * Use `validator.errorTypes` from `/forms`. */
  errorMessages: PropTypes.shape(errorMessagesShape),
  /** When `true`, `onSubmit` will be disabled if the `Form` is in error state. */
  errorsDisableSubmit: PropTypes.bool,
  /** A function that gives you control over how form state updates effect
   * other parts of form state. Gets called with every state update and
   * merges whatever you return with the form state. */
  fieldStateReducer: PropTypes.func,
  /** If you need to wrap your fields in a different element.
   * Nested `form` elements are not proper markup. If you need nested `Form`'s,
   * this prop allows you to wrap your inner fields in a `div` instead. */
  formElement: PropTypes.string,
  /** Control Prop. Used to control the error state of the form. Must pass `onUpdate` prop. */
  hasErrors: PropTypes.bool,
  /** Object with initialState for each field in the form (**including ignored or hidden fields**).
   *  Or a function that returns an object. Requried if not using any control props. */
  initialFieldState: PropTypes.oneOfType([PropTypes.objectOf(PropTypes.oneOfType([PropTypes.shape({
    error: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
    ignore: PropTypes.bool,
    required: PropTypes.bool,
    value: PropTypes.any
  }), PropTypes.any]).isRequired), PropTypes.func]),
  /** Useful if your passing a custom `formElement` and
   * still need to handle certain keyDown events.
   * The `Form` already handles the `enter` key for you. */
  onKeyDown: PropTypes.func,
  /** `function` that is handed to the `form` element.
   * Receives an object with all of the form state and actions minus `initialize`. */
  onSubmit: PropTypes.func
};
Form.defaultProps = {
  autoComplete: 'on',
  className: '',
  diff: false,
  errorsDisableSubmit: true,
  formElement: 'form'
};
export { Form, FormValidationError };